function getCurrentLanguage(): string {
  try {
    const cookie = document.cookie.split('; ').find(r => r.startsWith('lang='));
    if (cookie) {
      const val = cookie.split('=')[1];
      return val === "at" ? "de" : val;
    }
    return localStorage.getItem("vektor_language") ?? "en";
  } catch { return "en"; }
}

export interface RawItemDef {
  id: number;
  displayName: string;
  name: string;
  stackSize: number;
  variations?: { metadata: number; displayName: string }[];
}

export interface ItemDef {
  id: number;
  metadata: number;
  key: string;
  displayName: string;
  name: string;
  stackSize: number;
}

export interface Ingredient {
  id: number;
  metadata: number;
  key: string;
  count: number;
}

export interface CraftingRecipe {
  resultId: number;
  resultCount: number;
  resultMetadata: number;
  resultKey: string;
  ingredients: Ingredient[];
  inShape?: RawIngredient[][];
  rawIngredients?: RawIngredient[];
}

export interface GridCell {
  key: string;
  id: number;
  metadata: number;
  qty: number;
}

export function itemKey(id: number, metadata: number, hash?: string): string {
  const base = metadata >= 0 ? `${id}:${metadata}` : `${id}`;
  return hash ? `${base}:${hash}` : base;
}

export function parseItemKey(key: string): { id: number; metadata: number; hash?: string } | null {
  const parts = key.split(":");
  if (parts.length < 2 || parts.length > 3) return null;
  const id = parseInt(parts[0]);
  const metadata = parseInt(parts[1]);
  if (isNaN(id) || isNaN(metadata)) return null;
  return { id, metadata, hash: parts[2] };
}

type RawIngredient = number | { id: number; metadata?: number } | null;

function customRecipeKey(plainName: string, resultId: number): string {
  return `${plainName}|${resultId}`;
}

export class CraftingResolverService {
  private items: Map<string, ItemDef> = new Map();
  private recipes: Map<number, CraftingRecipe[]> = new Map();
  private allRecipes: CraftingRecipe[] = [];
  private searchIndex: Map<string, string> = new Map(); // itemKey → lowercased searchable text
  private enNames: Record<string, string> = {};
  private deNames: Record<string, string> = {};
  private customNames: Map<string, { name: string; count: number }> = new Map();
  // GrieferGames recipes, keyed plain name + resultId exactly like ItemRegistry.CustomRecipeKey on the
  // backend: the same named item exists under several NBT hashes, so a hash cannot key a recipe.
  private customRecipeKeys: Set<string> = new Set();
  private ready = false;

  // Feed known custom items (from the global registry) so their names resolve app-wide.
  // A base id:metadata only resolves when exactly one custom item uses it (avoids mislabeling).
  ingestCustomItems(items: { itemId: number; metadata: number; displayName?: string | null }[]) {
    for (const c of items) {
      if (!c.displayName) continue;
      const key = itemKey(c.itemId, c.metadata);
      const name = c.displayName.replace(/§[0-9a-fk-or]/gi, "");
      const cur = this.customNames.get(key);
      if (!cur) this.customNames.set(key, { name, count: 1 });
      else if (cur.name !== name) cur.count += 1;
    }
  }

  getCustomName(itemId: number, metadata: number): string | undefined {
    const e = this.customNames.get(itemKey(itemId, metadata));
    return e && e.count === 1 ? e.name : undefined;
  }

  async initialize() {
    const [itemsRes, recipesRes, customRecipesRes] = await Promise.all([
      fetch("/data/items.json"),
      fetch("/data/recipes.json"),
      fetch("/data/griefergames-recipes.json").catch(() => null),
    ]);
    const rawItems: RawItemDef[] = await itemsRes.json();
    const recipesObj: Record<string, RawRecipe[]> = await recipesRes.json();

    if (customRecipesRes?.ok) {
      try {
        const customRecipes: Record<string, { name: string; resultId: number }> = await customRecipesRes.json();
        for (const recipe of Object.values(customRecipes))
          this.customRecipeKeys.add(customRecipeKey(recipe.name, recipe.resultId));
      } catch {}
    }

    try {
      const enMod = await import("../en_US.json");
      this.enNames = enMod.default ?? enMod;
    } catch {}
    try {
      const deMod = await import("../de_DE.json");
      this.deNames = deMod.default ?? deMod;
    } catch {}
    const enNames = this.enNames;
    const deNames = this.deNames;

    for (const raw of rawItems) {
      if (raw.variations && raw.variations.length > 0) {
        for (const v of raw.variations) {
          const key = itemKey(raw.id, v.metadata);
          const item: ItemDef = {
            id: raw.id,
            metadata: v.metadata,
            key,
            displayName: v.displayName,
            name: raw.name,
            stackSize: raw.stackSize,
          };
          this.items.set(key, item);
          this.buildSearchEntry(item, enNames, deNames);
        }
      } else {
        const key = itemKey(raw.id, 0);
        const item: ItemDef = {
          id: raw.id,
          metadata: 0,
          key,
          displayName: raw.displayName,
          name: raw.name,
          stackSize: raw.stackSize,
        };
        this.items.set(key, item);
        this.buildSearchEntry(item, enNames, deNames);
      }
    }

    for (const [resultIdStr, variants] of Object.entries(recipesObj)) {
      const resultId = Number(resultIdStr);
      const parsed: CraftingRecipe[] = [];
      for (const raw of variants) {
        const recipe = this.parseRecipe(resultId, raw);
        if (recipe) parsed.push(recipe);
      }
      if (parsed.length > 0) this.recipes.set(resultId, parsed);
    }

    this.allRecipes = [];
    for (const list of this.recipes.values()) {
      this.allRecipes.push(...list);
    }

    this.ready = true;
  }

  private buildSearchEntry(item: ItemDef, enNames: Record<string, string>, deNames: Record<string, string>) {
    const mcKey = `minecraft:${item.name}:${item.metadata}`;
    const parts = [
      item.displayName,
      item.name.replace(/_/g, " "),
      enNames[mcKey] ?? "",
      deNames[mcKey] ?? "",
    ];
    this.searchIndex.set(item.key, parts.join(" ").toLowerCase());
  }

  isReady() {
    return this.ready;
  }

  hasCustomRecipe(plainName: string, itemId: number): boolean {
    return this.customRecipeKeys.has(customRecipeKey(plainName, itemId));
  }

  getAllItems(): ItemDef[] {
    return Array.from(this.items.values());
  }

  // Keys that carry an NBT hash (a custom item's "160:14:dbedaa3f") name the same material as their
  // "160:14" base, which is all items.json knows about.
  getItem(key: string): ItemDef | undefined {
    const direct = this.items.get(key);
    if (direct) return direct;
    const parsed = parseItemKey(key);
    return parsed?.hash ? this.items.get(itemKey(parsed.id, parsed.metadata)) : undefined;
  }

  getLocalizedName(key: string, lang?: string): string {
    const item = this.getItem(key);
    if (!item) return key;
    return this.getMinecraftName(`minecraft:${item.name}:${item.metadata}`, lang) ?? item.displayName;
  }

  // The Minecraft language tables (de_DE.json, en_US.json) by their own key. getLocalizedName covers
  // item keys; the potion effect and modifier lines only exist here.
  getMinecraftName(mcKey: string, lang?: string): string | undefined {
    const userLang = lang ?? getCurrentLanguage();
    if (userLang.startsWith("de")) return this.deNames[mcKey] ?? this.enNames[mcKey];
    return this.enNames[mcKey];
  }

  getItemById(id: number, metadata: number = 0): ItemDef | undefined {
    return this.items.get(itemKey(id, metadata));
  }

  /** Every metadata an item is registered under, in metadata order. One entry means it has no variants. */
  getVariantsById(id: number): ItemDef[] {
    return this.getAllItems()
      .filter((item) => item.id === id)
      .sort((left, right) => left.metadata - right.metadata);
  }

  getRecipe(itemId: number, metadata: number = -1): CraftingRecipe | undefined {
    const list = this.recipes.get(itemId);
    if (!list) return undefined;
    if (metadata >= 0) {
      return list.find((r) => r.resultMetadata === metadata);
    }
    return list[0];
  }

  getRecipeByKey(key: string): CraftingRecipe | undefined {
    const item = this.items.get(key);
    if (!item) return undefined;
    return this.getRecipe(item.id, item.metadata);
  }

  hasRecipe(itemId: number): boolean {
    return this.recipes.has(itemId);
  }

  hasRecipeForKey(key: string): boolean {
    const item = this.items.get(key);
    if (!item) return false;
    return this.getRecipe(item.id, item.metadata) !== undefined;
  }

  /** The lowercased English, German and internal names an item is found by; empty for an unknown key. */
  searchText(key: string): string {
    return this.searchIndex.get(key) ?? "";
  }

  matchesSearch(key: string, query: string): boolean {
    const entry = this.searchText(key);
    if (!entry) return false;
    return entry.includes(query.toLowerCase());
  }

  searchItems(query: string): ItemDef[] {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    return this.getAllItems().filter(item => this.matchesSearch(item.key, q));
  }

  matchGrid(grid: (GridCell | null)[]): CraftingRecipe | null {
    const grid2d = [grid.slice(0, 3), grid.slice(3, 6), grid.slice(6, 9)];
    const trimmed = trimGrid(grid2d);
    if (!trimmed) return null;

    for (const recipe of this.allRecipes) {
      if (recipe.inShape) {
        if (matchShaped(trimmed, recipe.inShape)) return recipe;
      } else if (recipe.rawIngredients) {
        if (matchShapeless(grid, recipe.rawIngredients)) return recipe;
      }
    }
    return null;
  }

  maxBatchesFromGrid(grid: (GridCell | null)[], recipe: CraftingRecipe): number {
    if (recipe.inShape) {
      return maxBatchesShaped(grid, recipe.inShape);
    } else if (recipe.rawIngredients) {
      return maxBatchesShapeless(grid, recipe.rawIngredients);
    }
    return 0;
  }

  private parseRecipe(resultId: number, raw: RawRecipe): CraftingRecipe | null {
    const resultCount = raw.result?.count ?? 1;
    const resultMetadata = raw.result?.metadata ?? 0;
    const ingredientMap = new Map<string, Ingredient>();

    if (raw.inShape) {
      for (const row of raw.inShape) {
        for (const cell of row) {
          const ing = this.parseIngElement(cell);
          if (ing) this.accumulate(ingredientMap, ing);
        }
      }
    } else if (raw.ingredients) {
      for (const el of raw.ingredients) {
        const ing = this.parseIngElement(el);
        if (ing) this.accumulate(ingredientMap, ing);
      }
    }

    if (ingredientMap.size === 0) return null;

    return {
      resultId,
      resultCount,
      resultMetadata,
      resultKey: itemKey(resultId, resultMetadata),
      ingredients: Array.from(ingredientMap.values()),
      inShape: raw.inShape ?? undefined,
      rawIngredients: raw.ingredients ?? undefined,
    };
  }

  private parseIngElement(
    el: number | { id: number; metadata?: number } | null
  ): Ingredient | null {
    if (el == null) return null;
    if (typeof el === "number") {
      if (el < 0) return null;
      return { id: el, metadata: -1, key: itemKey(el, -1), count: 1 };
    }
    if (el.id < 0) return null;
    const meta = el.metadata ?? -1;
    return { id: el.id, metadata: meta, key: itemKey(el.id, meta), count: 1 };
  }

  private accumulate(map: Map<string, Ingredient>, ing: Ingredient) {
    const existing = map.get(ing.key);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(ing.key, { ...ing });
    }
  }
}

function rawIngToIdMeta(el: RawIngredient): { id: number; metadata: number } | null {
  if (el == null) return null;
  if (typeof el === "number") return el < 0 ? null : { id: el, metadata: -1 };
  return el.id < 0 ? null : { id: el.id, metadata: el.metadata ?? -1 };
}

function cellMatchesRecipeSlot(cell: GridCell | null, slot: RawIngredient): boolean {
  const req = rawIngToIdMeta(slot);
  if (!req && !cell) return true;
  if (!req || !cell) return false;
  if (req.id !== cell.id) return false;
  if (req.metadata >= 0 && req.metadata !== cell.metadata) return false;
  return true;
}

function trimGrid(
  grid: (GridCell | null)[][]
): { cells: (GridCell | null)[][]; rows: number; cols: number } | null {
  let minR = 3, maxR = -1, minC = 3, maxC = -1;
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      if (grid[r][c]) {
        minR = Math.min(minR, r);
        maxR = Math.max(maxR, r);
        minC = Math.min(minC, c);
        maxC = Math.max(maxC, c);
      }
    }
  }
  if (maxR < 0) return null;
  const rows = maxR - minR + 1;
  const cols = maxC - minC + 1;
  const cells: (GridCell | null)[][] = [];
  for (let r = minR; r <= maxR; r++) {
    cells.push(grid[r].slice(minC, minC + cols));
  }
  return { cells, rows, cols };
}

function matchShaped(
  trimmed: { cells: (GridCell | null)[][]; rows: number; cols: number },
  inShape: RawIngredient[][]
): boolean {
  const shapeRows = inShape.length;
  const shapeCols = Math.max(...inShape.map((r) => r.length));
  if (trimmed.rows !== shapeRows || trimmed.cols !== shapeCols) return false;

  for (let r = 0; r < shapeRows; r++) {
    for (let c = 0; c < shapeCols; c++) {
      const recipeSlot = inShape[r]?.[c] ?? null;
      const gridCell = trimmed.cells[r]?.[c] ?? null;
      if (!cellMatchesRecipeSlot(gridCell, recipeSlot)) return false;
    }
  }
  return true;
}

function matchShapeless(grid: (GridCell | null)[], rawIngredients: RawIngredient[]): boolean {
  const gridItems: string[] = [];
  for (const cell of grid) {
    if (cell) {
      for (let i = 0; i < cell.qty; i++) gridItems.push(cell.key);
    }
  }

  const needed: string[] = [];
  for (const el of rawIngredients) {
    const parsed = rawIngToIdMeta(el);
    if (parsed) needed.push(itemKey(parsed.id, parsed.metadata));
  }

  if (gridItems.length !== needed.length) return false;

  const gridBag = new Map<string, number>();
  for (const k of gridItems) gridBag.set(k, (gridBag.get(k) ?? 0) + 1);

  const needBag = new Map<string, number>();
  for (const k of needed) needBag.set(k, (needBag.get(k) ?? 0) + 1);

  if (gridBag.size !== needBag.size) return false;
  for (const [k, v] of needBag) {
    if ((gridBag.get(k) ?? 0) !== v) return false;
  }
  return true;
}

function maxBatchesShaped(grid: (GridCell | null)[], inShape: RawIngredient[][]): number {
  const grid2d = [grid.slice(0, 3), grid.slice(3, 6), grid.slice(6, 9)];
  const trimmed = trimGrid(grid2d);
  if (!trimmed) return 0;

  const shapeRows = inShape.length;
  const shapeCols = Math.max(...inShape.map((r) => r.length));
  if (trimmed.rows !== shapeRows || trimmed.cols !== shapeCols) return 0;

  let minBatches = Infinity;
  for (let r = 0; r < shapeRows; r++) {
    for (let c = 0; c < shapeCols; c++) {
      const req = rawIngToIdMeta(inShape[r]?.[c] ?? null);
      if (!req) continue;
      const cell = trimmed.cells[r]?.[c];
      if (!cell) return 0;
      minBatches = Math.min(minBatches, cell.qty);
    }
  }
  return minBatches === Infinity ? 0 : minBatches;
}

function maxBatchesShapeless(grid: (GridCell | null)[], rawIngredients: RawIngredient[]): number {
  const needBag = new Map<string, number>();
  for (const el of rawIngredients) {
    const parsed = rawIngToIdMeta(el);
    if (parsed) {
      const k = itemKey(parsed.id, parsed.metadata);
      needBag.set(k, (needBag.get(k) ?? 0) + 1);
    }
  }

  const gridBag = new Map<string, number>();
  for (const cell of grid) {
    if (cell) gridBag.set(cell.key, (gridBag.get(cell.key) ?? 0) + cell.qty);
  }

  let minBatches = Infinity;
  for (const [k, need] of needBag) {
    const have = gridBag.get(k) ?? 0;
    minBatches = Math.min(minBatches, Math.floor(have / need));
  }
  return minBatches === Infinity ? 0 : minBatches;
}

interface RawRecipe {
  inShape?: (number | { id: number; metadata?: number } | null)[][];
  ingredients?: (number | { id: number; metadata?: number } | null)[];
  result?: { count?: number; id?: number; metadata?: number };
}
